// Shared style for export buttons.  Kept for callers that build their own
// bars; PlotExportBar uses its own inline style.
export const exportBtn = (c) => ({
  padding: '6px 12px',
  fontSize: 12,
  cursor: 'pointer',
  background: c.btnSecondary,
  border: `1px solid ${c.border}`,
  color: c.btnSecondaryText,
  borderRadius: 4,
  fontFamily: 'IBM Plex Mono, monospace',
});

// Replace dark-theme colors with publication-friendly dark-on-white
// equivalents so PNG / SVG exports read on a printed page.  Keys are
// lower-cased; values are the publication colour.
//
// IMPORTANT: do NOT include '#ffffff' / 'white' here — the white page
// background and the bgRect we inject would both get clobbered.  Text
// colour is forced to dark below in a dedicated pass.
const COLOR_MAP = {
  // Dark theme backgrounds / panels → near-white
  '#0d1117': '#ffffff',
  '#0a0e1a': '#ffffff',
  '#161b22': '#ffffff',
  '#21262d': '#f8f9fa',
  // Cyan accent → dark blue
  '#00d4ff': '#0066cc',
  '#00a8cc': '#0052a3',
  // Green → dark green
  '#00ff88': '#007744',
  '#3fb950': '#006633',
  '#22c55e': '#006633',
  // Gold → dark amber
  '#ffd700': '#cc8800',
  '#d29922': '#aa7700',
  // Red stays red but darker
  '#ff6b6b': '#cc2222',
  '#f85149': '#bb1111',
  '#f87171': '#bb1111',
  // Magenta / orange peak palette → desaturated dark variants
  '#ff00ff': '#990099',
  '#ff8800': '#aa5500',
  '#88ff00': '#558800',
  '#8800ff': '#5500aa',
  // Grey text → dark grey
  '#8b949e': '#444444',
  '#a0aec0': '#333333',
  '#e6edf3': '#111111',
};

const stamp = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_` +
         `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
};

const escapeCell = (v) => {
  if (v == null) return '';
  const s = typeof v === 'number' && Number.isFinite(v) ? String(v) : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const exportCSV = (data, filename) => {
  if (!Array.isArray(data) || data.length === 0) return;
  const headers = Object.keys(data[0]);
  const lines = [headers.join(',')];
  for (const row of data) {
    lines.push(headers.map(h => escapeCell(row[h])).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}_${stamp()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// Build a publication-friendly clone of the first <svg> inside `containerId`.
// Returns { clone, width, height } or null if there's no SVG to clone.
function buildExportClone(containerId) {
  const container = document.getElementById(containerId);
  if (!container) {
    console.error('Chart not found:', containerId);
    return null;
  }
  const svg = container.querySelector('svg');
  if (!svg) {
    console.error('No SVG in chart:', containerId);
    return null;
  }

  const rect = svg.getBoundingClientRect();
  const w = Math.round(rect.width || svg.clientWidth || 800);
  const h = Math.round(rect.height || svg.clientHeight || 400);

  const clone = svg.cloneNode(true);
  clone.setAttribute('width',  String(w));
  clone.setAttribute('height', String(h));
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

  // Map dark-theme palette to dark-on-white equivalents.  Done BEFORE we
  // inject the white background rect so the rect's fill is not clobbered.
  const remap = (attr) => (el) => {
    const v = (el.getAttribute(attr) || '').toLowerCase();
    if (COLOR_MAP[v]) el.setAttribute(attr, COLOR_MAP[v]);
  };
  clone.querySelectorAll('[stroke]').forEach(remap('stroke'));
  clone.querySelectorAll('[fill]').forEach(remap('fill'));

  // Style-attribute remap — Recharts and inline styles often carry the
  // colour there.  String-replace each known dark colour.
  const entries = Object.entries(COLOR_MAP);
  clone.querySelectorAll('[style]').forEach(el => {
    let s = el.getAttribute('style');
    if (!s) return;
    let changed = false;
    for (const [from, to] of entries) {
      if (s.toLowerCase().includes(from)) {
        // case-insensitive global replace
        s = s.replace(new RegExp(from.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&'), 'gi'), to);
        changed = true;
      }
    }
    if (changed) el.setAttribute('style', s);
  });

  // All text becomes dark grey for contrast (handles white-on-dark labels).
  clone.querySelectorAll('text, tspan').forEach(t => {
    t.setAttribute('fill', '#222222');
    t.style.fill = '#222222';
  });

  // Recharts axis lines and ticks → dark grey
  clone.querySelectorAll(
    'line.recharts-cartesian-axis-line, ' +
    'line.recharts-cartesian-axis-tick-line'
  ).forEach(l => l.setAttribute('stroke', '#222222'));

  // Recharts grid → light grey
  clone.querySelectorAll(
    'line.recharts-cartesian-grid-horizontal, ' +
    'line.recharts-cartesian-grid-vertical, ' +
    'path.recharts-cartesian-grid-horizontal, ' +
    'path.recharts-cartesian-grid-vertical'
  ).forEach(l => l.setAttribute('stroke', '#cccccc'));

  // Opaque white background — inserted LAST so it sits behind everything
  // and is not touched by the colour remap above.
  const ns = 'http://www.w3.org/2000/svg';
  const bg = document.createElementNS(ns, 'rect');
  bg.setAttribute('width',  String(w));
  bg.setAttribute('height', String(h));
  bg.setAttribute('fill', '#ffffff');
  clone.insertBefore(bg, clone.firstChild);

  return { clone, width: w, height: h };
}

// Publication-quality SVG of a single chart.
export const exportSVG = (containerId, filename) => {
  const built = buildExportClone(containerId);
  if (!built) return;
  const data = new XMLSerializer().serializeToString(built.clone);
  const blob = new Blob([data], { type: 'image/svg+xml;charset=utf-8' });
  const link = document.createElement('a');
  link.download = `JVISTA_${filename}_${stamp()}.svg`;
  link.href = URL.createObjectURL(blob);
  link.click();
  URL.revokeObjectURL(link.href);
};

// Publication-quality PNG of a single chart, rasterised at 3× scale
// (~300 DPI) on a white background using SVG → Image → Canvas.
// Avoids html2canvas entirely.
export const exportPNG = (containerId, filename) => {
  const built = buildExportClone(containerId);
  if (!built) return;
  const { clone, width, height } = built;

  const svgStr = new XMLSerializer().serializeToString(clone);
  const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const img = new Image();
  img.onload = () => {
    const scale = 3; // ~300 DPI
    const canvas = document.createElement('canvas');
    canvas.width  = Math.round(width  * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(url);
    const link = document.createElement('a');
    link.download = `JVISTA_${filename}_${stamp()}.png`;
    link.href = canvas.toDataURL('image/png', 1.0);
    link.click();
  };
  img.onerror = (e) => {
    URL.revokeObjectURL(url);
    console.error('PNG export: SVG → Image conversion failed', e);
  };
  img.src = url;
};
